import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

type TrainingLevel = 'beginner' | 'intermediate' | 'advanced' | 'pro';

interface HomeworkItem {
  id: string;
  trainingLevel: TrainingLevel;
  title: string;
  description: string;
  practiceAtHome: boolean;
  requiresCourt: boolean;
  isActive: boolean;
  displayOrder: number;
}

interface AdminTrainingHomeworkProps {
  sport: string;
}

const emptyForm = {
  trainingLevel: 'beginner' as TrainingLevel,
  title: '',
  description: '',
  practiceAtHome: true,
  requiresCourt: false,
  isActive: true,
  displayOrder: 0,
};

const AdminTrainingHomework: React.FC<AdminTrainingHomeworkProps> = ({ sport }) => {
  const [items, setItems] = useState<HomeworkItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<HomeworkItem | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ sport });
      const response = await fetch(`/api/admin/homework-items?${params.toString()}`);
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to load homework items');
        setItems([]);
        return;
      }
      setItems(result.data || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load homework items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadItems();
  }, [sport]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (item: HomeworkItem) => {
    setEditing(item);
    setForm({
      trainingLevel: item.trainingLevel,
      title: item.title,
      description: item.description || '',
      practiceAtHome: item.practiceAtHome,
      requiresCourt: item.requiresCourt,
      isActive: item.isActive,
      displayOrder: item.displayOrder || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    try {
      const payload = {
        sport,
        ...form,
      };
      const url = editing ? `/api/admin/homework-items/${editing.id}` : '/api/admin/homework-items';
      const method = editing ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to save homework item');
        return;
      }
      setSuccess(editing ? 'Homework item updated' : 'Homework item created');
      setDialogOpen(false);
      await loadItems();
    } catch (e: any) {
      setError(e.message || 'Failed to save homework item');
    }
  };

  const handleDelete = async (id: string) => {
    const ok = window.confirm('Delete this homework item?');
    if (!ok) return;
    try {
      const response = await fetch(`/api/admin/homework-items/${id}`, { method: 'DELETE' });
      const result = await response.json();
      if (!result.success) {
        setError(result.message || 'Failed to delete homework item');
        return;
      }
      setSuccess('Homework item deleted');
      await loadItems();
    } catch (e: any) {
      setError(e.message || 'Failed to delete homework item');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Homework Master - {sport}
        </Typography>
        <Button variant="contained" onClick={openCreate}>
          Add Homework Item
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          {loading ? (
            <Typography variant="body2">Loading...</Typography>
          ) : items.length === 0 ? (
            <Alert severity="info">No homework items yet. Add one to begin assignment flow.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Level</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Chip size="small" label={item.trainingLevel} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                      {item.description && (
                        <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {item.practiceAtHome && <Chip size="small" label="Home" />}
                        {item.requiresCourt && <Chip size="small" label="Court" />}
                      </Stack>
                    </TableCell>
                    <TableCell>{item.isActive ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openEdit(item)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(item.id)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Homework Item' : 'Add Homework Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Training Level"
              value={form.trainingLevel}
              onChange={(e) => setForm((prev) => ({ ...prev, trainingLevel: e.target.value as TrainingLevel }))}
              fullWidth
            >
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
            </TextField>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Display Order"
              type="number"
              value={form.displayOrder}
              onChange={(e) => setForm((prev) => ({ ...prev, displayOrder: Number(e.target.value) || 0 }))}
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={form.practiceAtHome} onChange={(e) => setForm((prev) => ({ ...prev, practiceAtHome: e.target.checked }))} />}
              label="Can be practiced at home (without coach/court)"
            />
            <FormControlLabel
              control={<Checkbox checked={form.requiresCourt} onChange={(e) => setForm((prev) => ({ ...prev, requiresCourt: e.target.checked }))} />}
              label="Requires own court access"
            />
            <FormControlLabel
              control={<Checkbox checked={form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTrainingHomework;
